/**
 * FmChart — the one chart element.
 *
 * Takes a typed `FmChartSpec` and renders it inside the shared chrome
 * (`FmChartBlock`): title, legend inline with the plot, axis titles carrying
 * the unit once, caption/takeaway/source outside the frame, one tooltip, and
 * the Recharts accessibility layer. A species value the palette refuses
 * (unknown code, or a name instead of a code) renders the chart's error state
 * naming that value instead of a default colour.
 */
import React from 'react';
import type { FmDataDisplayRule } from '../../../types/fm-data-value';
import type { FmLegendEntry } from '../../../types/fm-legend';
import { FmBanner } from '../FmBanner';
import { FmLegend } from '../FmLegend';
import type { FmChartBlockPoint } from './FmChartBlock';
import { FmChartBlock } from './FmChartBlock';
import type { FmChartSize, FmChartSpec } from './chartTypes';
import { FmBarChart } from './FmBarChart';
import { FmCashflowChart } from './FmCashflowChart';
import { FmCorridorRowsChart } from './FmCorridorRowsChart';
import { FmDiameterHistogram } from './FmDiameterHistogram';
import { FmDonutChart } from './FmDonutChart';
import { FmGrowthChart } from './FmGrowthChart';
import { FmPriceChart, type FmPriceChartRow } from './FmPriceChart';
import { FmReferenceDeviationChart } from './FmReferenceDeviationChart';
import { FmScatterChart } from './FmScatterChart';
import { ChartPaletteError, SERIES_PATTERN_DASH } from './palette';
import { resolveSeriesColor } from './seriesColor';
import { chartLegend, timeSeriesPattern } from './chartLegend';

export interface FmChartProps {
  title: string;
  /** `null` only with `error`: the caller refused to build the spec. */
  chart: FmChartSpec | null;
  /** Unit of the plotted value, printed once (in the axis title where there is one). */
  unit: string;
  caption: React.ReactNode;
  takeaway: React.ReactNode;
  contextDetail?: React.ReactNode;
  source?: React.ReactNode;
  explanationTitle?: string;
  /** The host already printed the title. */
  hosted?: boolean;
  variant?: 'figure' | 'story';
  size?: FmChartSize;
  valueKind?: FmDataDisplayRule['kind'];
  loading?: boolean;
  /** A refusal the caller hit while building the spec (e.g. a species name). */
  error?: string;
  emptyText?: string;
  locale?: string;
  'data-testid'?: string;
}

/**
 * Whether the plot itself already shows the unit: cartesian kinds in the axis
 * title, and a donut whose unit is a
 * share in its percentage legend. Only a donut of absolute values leaves the
 * unit to the caption rail.
 */
function plotPrintsUnit(kind: FmChartSpec['kind'], unit: string): boolean {
  if (kind === 'donut') return unit.trim() === '%';
  return true;
}

function point(id: string, label: string, value: number | null): FmChartBlockPoint {
  return { id, label, value };
}

/** The first finite value a row serves among `keys`, or null when it serves none. */
function firstFinite<Datum extends object>(
  row: Datum,
  keys: readonly (keyof Datum)[],
): number | null {
  for (const key of keys) {
    const value: unknown = row[key];
    if (typeof value === 'number' && Number.isFinite(value)) return value;
  }
  return null;
}

function rowPoints<Datum extends object>(
  rows: readonly Datum[],
  xKey: keyof Datum,
  keys: readonly (keyof Datum)[],
): FmChartBlockPoint[] {
  return rows.map((row, index) => point(String(index), String(row[xKey]), firstFinite(row, keys)));
}

/**
 * One availability point per plotted datum. A datum with no served value is
 * null, so a chart whose rows serve nothing renders the unavailable state.
 */
function chartPoints(chart: FmChartSpec): FmChartBlockPoint[] {
  switch (chart.kind) {
    case 'bar':
      return rowPoints(
        chart.data,
        chart.xKey,
        chart.series.map((entry) => entry.dataKey),
      );
    case 'growth':
      return rowPoints(
        chart.data,
        chart.xKey,
        chart.series.map((entry) => entry.key),
      );
    case 'time-series':
      return rowPoints(
        chart.data,
        chart.xKey,
        chart.series.map((entry) => entry.dataKey),
      );
    case 'cashflow':
      return rowPoints(chart.data, 'period', ['income', 'cost', 'net']);
    case 'reference-deviation':
      return rowPoints(chart.points, 'label', ['observed', 'reference']);
    case 'histogram':
      return chart.distributions.flatMap((entry) =>
        entry.bins.map((bin) =>
          point(`${entry.speciesCode}-${bin.diameter_cm}`, entry.label, bin.n_per_ha),
        ),
      );
    case 'donut':
      return chart.slices
        .filter((slice) => slice.value > 0)
        .map((slice) => point(slice.id, slice.label, slice.value));
    case 'scatter':
      return chart.groups.flatMap((group) =>
        group.points.map((entry, index) => point(`${group.id}-${index}`, group.label, entry.y)),
      );
    case 'corridor-rows':
      return chart.rows.map((row) => point(row.id, row.label, row.residual ?? row.bandLo));
  }
}

function timeSeriesRows(chart: Extract<FmChartSpec, { kind: 'time-series' }>): FmPriceChartRow[] {
  return chart.data.map((row) => {
    const values: Record<string, number | undefined> = {};
    chart.series.forEach((entry) => {
      const value = row[entry.dataKey];
      values[entry.dataKey] =
        typeof value === 'number' && Number.isFinite(value) ? value : undefined;
    });
    return Object.assign({ date: String(row[chart.xKey] ?? '') }, values);
  });
}

function renderLeaf(chart: FmChartSpec, size: FmChartSize, testId: string): React.ReactNode {
  switch (chart.kind) {
    case 'bar':
      return <FmBarChart {...chart} size={size} data-testid={`${testId}-bar-chart`} />;
    case 'histogram':
      return <FmDiameterHistogram {...chart} data-testid={`${testId}-diameter-histogram`} />;
    case 'growth':
      return <FmGrowthChart {...chart} size={size} data-testid={`${testId}-growth-chart`} />;
    case 'time-series':
      return (
        <FmPriceChart
          data={timeSeriesRows(chart)}
          overlaySeries={chart.series.map((entry, index) => ({
            dataKey: entry.dataKey,
            name: entry.label,
            color: resolveSeriesColor(entry.color),
            variant: entry.variant,
            dash: SERIES_PATTERN_DASH[timeSeriesPattern(chart, index)] ?? '',
          }))}
          onDateRangeChange={chart.onRangeChange}
          formatXAxisTick={chart.formatXAxisTick}
          formatYAxisTick={chart.formatTick}
          yAxisLabel={chart.yLabel}
          formatTooltipValue={(value) => (value === null ? '—' : chart.formatValue(value))}
          endLabels={chart.legend === 'none'}
          data-testid={`${testId}-time-series`}
        />
      );
    case 'donut':
      return <FmDonutChart {...chart} data-testid={`${testId}-share-donut`} />;
    case 'scatter':
      return <FmScatterChart {...chart} size={size} data-testid={`${testId}-scatter`} />;
    case 'cashflow':
      return <FmCashflowChart {...chart} size={size} data-testid={`${testId}-cashflow`} />;
    case 'corridor-rows':
      return <FmCorridorRowsChart {...chart} data-testid={`${testId}-corridor-rows`} />;
    case 'reference-deviation':
      return (
        <FmReferenceDeviationChart
          {...chart}
          size={size}
          data-testid={`${testId}-reference-deviation`}
        />
      );
  }
}

type ResolvedChart =
  { ok: true; legend: FmLegendEntry[]; leaf: React.ReactNode } | { ok: false; message: string };

/**
 * Palette refusals surface as the chart's visible error state; any other
 * exception is a defect and propagates.
 */
function resolveChart(chart: FmChartSpec, size: FmChartSize, testId: string): ResolvedChart {
  try {
    return {
      ok: true,
      legend: chartLegend(chart),
      leaf: renderLeaf(chart, size, testId),
    };
  } catch (error) {
    if (error instanceof ChartPaletteError) return { ok: false, message: error.message };
    throw error;
  }
}

export interface FmChartPlotProps {
  chart: FmChartSpec;
  /** Accessible name of the plot; the host region prints the visible title. */
  ariaLabel: string;
  size?: FmChartSize;
  /** Test id of the inline legend; defaults to `<data-testid>-legend`. */
  legendTestId?: string;
  'data-testid': string;
}

/**
 * The plot of `FmChart` — inline legend, leaf and palette-refusal state —
 * for hosts that already own the title and explanation (workspace regions).
 */
export const FmChartPlot: React.FC<FmChartPlotProps> = ({
  chart,
  ariaLabel,
  size = 'lg',
  legendTestId,
  'data-testid': testId,
}) => {
  const resolved = resolveChart(chart, size, testId);
  if (!resolved.ok) return <FmBanner tone="error" title={resolved.message} />;
  return (
    <figure className="fm-chart-plot" aria-label={ariaLabel} data-testid={testId}>
      {resolved.legend.length > 0 ? (
        <FmLegend
          entries={resolved.legend}
          direction="wrap"
          data-testid={legendTestId ?? `${testId}-legend`}
        />
      ) : null}
      {resolved.leaf}
    </figure>
  );
};

export const FmChart: React.FC<FmChartProps> = ({
  title,
  chart,
  unit,
  caption,
  takeaway,
  contextDetail,
  source,
  explanationTitle,
  hosted,
  variant = 'figure',
  size = 'lg',
  valueKind,
  loading,
  error,
  emptyText,
  locale,
  'data-testid': testIdProp,
}) => {
  const testId = testIdProp ?? 'fm-chart';
  if (chart === null) {
    return (
      <FmChartBlock
        title={title}
        hosted={hosted}
        data={[]}
        unit={unit}
        caption={caption}
        takeaway={takeaway}
        error={error}
        emptyText={emptyText}
        variant={variant}
        data-testid={testId}
      />
    );
  }
  const resolved = resolveChart(chart, size, testId);
  return (
    <FmChartBlock
      title={title}
      hosted={hosted}
      data={chartPoints(chart)}
      unit={unit}
      unitInAxis={plotPrintsUnit(chart.kind, unit)}
      caption={caption}
      takeaway={takeaway}
      contextDetail={contextDetail}
      chart={resolved.ok ? resolved.leaf : undefined}
      legend={resolved.ok ? resolved.legend : []}
      error={error ?? (resolved.ok ? undefined : resolved.message)}
      valueKind={valueKind}
      sparseMode="chart"
      maxPlotWidth={1040}
      maxBarWidth={72}
      explanationTitle={explanationTitle}
      source={source}
      loading={loading}
      emptyText={emptyText}
      variant={variant}
      locale={locale}
      data-testid={testId}
    />
  );
};
